import type { CSSProperties, ReactNode } from "react";
import { Icons } from "@/components/ui/icons";
import { surfaceRoleAttributes } from "@/components/ui/surface-contract";
import { useThemeChoice } from "@/hooks/use-theme-choice";
import { cn } from "@/lib/cn";
import type { ThemeChoice } from "@/lib/theme";
import { cursorTextShadow } from "./agent-cursor-glass";
import {
  clampVoiceLevel,
  voiceGradientStyle,
  type VoiceGradientStatus,
} from "./voice-gradient-style";
import { VoiceGradientReflection } from "./voice-gradient-reflection";

export type VoiceListeningStatus = VoiceGradientStatus;

function capsuleWidth({
  camera,
  isLarge,
  showAgentName,
}: {
  camera: boolean;
  isLarge: boolean;
  showAgentName: boolean;
}) {
  if (showAgentName) {
    if (camera) return isLarge ? "232px" : "140px";
    return isLarge ? "172px" : "104px";
  }
  if (camera) return isLarge ? "176px" : "84px";
  return isLarge ? "112px" : "44px";
}

type VoiceListeningGlassProps = {
  color: string;
  volume?: number;
  status?: VoiceListeningStatus;
  camera?: boolean;
  agentName?: string;
  showAgentName?: boolean;
  className?: string;
  size?: "compact" | "large";
  surface?: "dark" | "light";
  theme?: ThemeChoice;
  testId?: string;
};

export function VoiceListeningGlass({
  color,
  volume = 0,
  status = "listening",
  camera = false,
  agentName = "Denker",
  showAgentName = false,
  className,
  size = "compact",
  surface,
  theme,
  testId = "voice-glass",
}: VoiceListeningGlassProps) {
  const activeTheme = useThemeChoice();
  const resolvedTheme = theme ?? activeTheme;
  const level = clampVoiceLevel(volume);
  const isLarge = size === "large";
  const isLight = surface === "light" || resolvedTheme === "light";
  const width = capsuleWidth({ camera, isLarge, showAgentName });
  const height = isLarge ? "96px" : "34px";
  const voiceActive = status !== "listening" || level > 0.01;
  const agentTextShadow = cursorTextShadow(color);
  const gradientStyle = voiceGradientStyle(status, color, level);

  return (
    <div
      data-testid={testId}
      data-agent-name={agentName}
      data-agent-color={color}
      data-status={status}
      data-camera={camera ? "true" : undefined}
      data-theme={resolvedTheme}
      data-motion={voiceActive ? "active" : "idle"}
      aria-hidden="true"
      {...surfaceRoleAttributes("rail", {
        nativeLevel: "island",
        nativeGroup: "voice-indicator",
      })}
      className={cn(
        "relative isolate flex items-center justify-center overflow-hidden bg-transparent shadow-none transition-[width,border-radius,filter] duration-200 ease-out motion-reduce:transition-none",
        "text-[var(--color-readable-overlay-primary)]",
        className,
      )}
      style={
        {
          width,
          height,
          borderRadius: isLarge ? "34px" : "17px",
          ...gradientStyle,
        } as CSSProperties
      }
    >
      <VoiceGradientReflection />
      <div
        className={cn(
          "relative z-10 flex items-center justify-center",
          showAgentName ? "w-full px-3" : camera ? "gap-2.5" : "gap-0",
        )}
      >
        {showAgentName ? (
          <>
            <span
              data-testid="voice-agent-name"
              className={cn(
                "min-w-0 flex-1 truncate pr-2 text-left font-semibold leading-none",
                "text-[var(--color-readable-overlay-primary)]",
                isLarge ? "text-lg" : "text-appkit-caption",
              )}
              style={{
                textShadow: isLight ? undefined : agentTextShadow,
              }}
            >
              {agentName}
            </span>
            <span
              data-testid="voice-control-group"
              className={cn(
                "flex shrink-0 items-center",
                isLarge ? "gap-3" : "gap-1.5",
              )}
            >
              <IconSlot size={isLarge ? "large" : "compact"}>
                <GlassIcon
                  icon="mic"
                  size={isLarge ? "large" : "compact"}
                  status={status}
                />
              </IconSlot>
              {camera && (
                <IconSlot size={isLarge ? "large" : "compact"}>
                  <GlassIcon
                    icon="camera"
                    size={isLarge ? "large" : "compact"}
                    status={status}
                  />
                </IconSlot>
              )}
            </span>
          </>
        ) : (
          <>
            <GlassIcon
              icon="mic"
              size={isLarge ? "large" : "compact"}
              status={status}
            />
            {camera && (
              <>
                <span
                  className={cn(
                    "h-5 w-px",
                    isLight && "bg-black/10",
                    isLarge && "h-9",
                  )}
                />
                <GlassIcon
                  icon="camera"
                  size={isLarge ? "large" : "compact"}
                  status={status}
                />
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function IconSlot({
  children,
  size,
}: {
  children: ReactNode;
  size: "compact" | "large";
}) {
  return (
    <span
      className={cn(
        "grid shrink-0 place-items-center",
        size === "large" ? "h-12 w-12" : "h-6 w-6",
      )}
    >
      {children}
    </span>
  );
}

function GlassIcon({
  icon,
  size,
  status,
}: {
  icon: "mic" | "camera";
  size: "compact" | "large";
  status: VoiceListeningStatus;
}) {
  const Icon = icon === "mic" ? Icons.Mic : Icons.Camera;
  const large = size === "large";

  return (
    <Icon
      data-testid={icon === "mic" ? "voice-mic-icon" : "voice-camera-icon"}
      className={cn(
        "drop-shadow-[0_1px_1px_rgba(0,0,0,0.34)]",
        status === "error" ? "text-red-100/85" : "text-secondary",
        large ? "h-10 w-10" : "h-5 w-5",
      )}
      style={{
        color: "var(--voice-glass-icon)",
      }}
    />
  );
}
